import React, { useState } from "react";
import { Loader2, Image as ImageIcon, PlayCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

/**
 * V2.0.88 — Admin backfill trigger card.
 *
 * Runs the `POST /api/admin/backfill-thumbnails` endpoint from the UI
 * so ops don't need to shell into a pod. Supports dry-run mode +
 * optional limit. Displays the returned summary inline.
 */
export default function BackfillThumbnailsCard() {
  const [dryRun, setDryRun] = useState(true);
  const [limit, setLimit] = useState("");
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState("");

  const run = async () => {
    setLoading(true); setError(""); setSummary(null);
    try {
      const body = { dry_run: dryRun };
      const n = parseInt(limit, 10);
      if (!Number.isNaN(n) && n > 0) body.limit = n;
      const r = await api.post("/admin/backfill-thumbnails", body);
      setSummary(r.data);
      toast.success(
        dryRun
          ? `Dry-run complete — would backfill ${r.data.entries_backfilled} entry(ies)`
          : `Backfilled ${r.data.entries_backfilled} entry(ies)`,
      );
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Backfill failed";
      setError(detail);
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="border border-zinc-800 bg-zinc-950 p-5 space-y-4"
      data-testid="admin-backfill-card"
    >
      <div className="flex items-center gap-2">
        <ImageIcon className="w-4 h-4 text-brass-400" />
        <h3 className="font-mono text-[10px] uppercase tracking-widest text-brass-400">
          // Backfill Thumbnails
        </h3>
      </div>
      <p className="text-zinc-400 text-xs leading-relaxed">
        Generates 300px preview thumbnails for any legacy image uploads that
        predate V2.0.87. Idempotent — entries with an existing thumbnail are
        skipped. Runs synchronously; expect ~500ms per image.
      </p>
      <div className="flex flex-wrap items-center gap-3">
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={dryRun}
            onChange={(e) => setDryRun(e.target.checked)}
            className="accent-brass-500 w-4 h-4"
            data-testid="admin-backfill-dry-run"
          />
          <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-400">
            Dry run
          </span>
        </label>
        <label className="inline-flex items-center gap-2">
          <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
            Limit
          </span>
          <input
            type="number"
            min="1"
            placeholder="all"
            value={limit}
            onChange={(e) => setLimit(e.target.value)}
            data-testid="admin-backfill-limit"
            className="bg-zinc-900 border border-zinc-800 focus:border-brass-500 px-2 py-1 w-20 text-xs font-mono text-zinc-200 outline-none"
          />
        </label>
        <button
          type="button"
          onClick={run}
          disabled={loading}
          data-testid="admin-backfill-run"
          className="ml-auto inline-flex items-center gap-1.5 bg-brass-600 hover:bg-brass-500 disabled:opacity-50 text-white font-mono text-[10px] uppercase tracking-widest px-4 py-2"
        >
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PlayCircle className="w-3.5 h-3.5" />}
          {dryRun ? "Preview" : "Run backfill"}
        </button>
      </div>
      {error && (
        <div className="font-mono text-[11px] text-rose-400 border border-rose-800/60 bg-rose-950/40 px-3 py-2">
          {error}
        </div>
      )}
      {summary && (
        <div
          className={
            "border px-4 py-3 text-xs font-mono leading-relaxed " +
            (summary.dry_run
              ? "border-brass-700/50 bg-brass-950/20 text-brass-100"
              : "border-emerald-700/50 bg-emerald-950/30 text-emerald-100")
          }
          data-testid="admin-backfill-summary"
        >
          <div className="flex items-center gap-1.5 mb-1.5">
            <CheckCircle2 className="w-3.5 h-3.5" />
            <span className="uppercase tracking-widest text-[10px]">
              {summary.dry_run ? "Dry-run complete" : "Backfill complete"}
            </span>
          </div>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px]">
            <StatRow label="Designs scanned" value={summary.scanned} />
            <StatRow label="Entries examined" value={summary.entries_examined} />
            <StatRow
              label={summary.dry_run ? "Would backfill" : "Backfilled"}
              value={summary.entries_backfilled}
              highlight
            />
            <StatRow label="Skipped (no bytes)" value={summary.entries_skipped_no_bytes} />
            <StatRow label="Skipped (mime)" value={summary.entries_skipped_unsupported} />
            <StatRow label="Errors" value={summary.errors?.length || 0} />
          </dl>
          {(summary.errors || []).length > 0 && (
            <details className="mt-2">
              <summary className="cursor-pointer text-[10px] uppercase tracking-widest text-rose-300">
                Error details
              </summary>
              <ul className="mt-1 space-y-0.5 pl-4">
                {summary.errors.map((e, i) => (
                  <li key={i} className="text-[10px] text-rose-200 truncate" title={e}>
                    {e}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

function StatRow({ label, value, highlight = false }) {
  return (
    <>
      <dt className="text-zinc-400">{label}</dt>
      <dd className={"text-right " + (highlight ? "text-brass-300 font-bold" : "")}>
        {(value || 0).toLocaleString()}
      </dd>
    </>
  );
}
